import { useNavigate } from "react-router-dom";
import { ArrowRight, Gear, Certificate } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

const MR = [
  ["Safety function integrity", "Protection of safety-related control functions against accidental or intentional corruption."],
  ["Safe state on violation", "Defined, verified behaviour when integrity, authentication or communication checks fail."],
  ["Safety software control", "Safety-affecting software items identified and version-controlled separately from general software."],
  ["Update validation", "Evidence that safety functions remain effective after every security update."],
  ["Safety parameter control", "Authenticated, privileged and logged changes to safety limits and interlocks."],
  ["Intervention traceability", "Records of bypasses, mode changes and parameter edits on safety-related functions."],
  ["Remote intervention safety", "Remote commands with a physical effect gated by interlocks or explicit confirmation."],
  ["Operator instructions", "Cybersecurity measures the operator must maintain, stated in the instructions."],
];

const UKCA = [
  ["No universal defaults (PSTI)", "Unique per-device credentials, or credentials the user must define before use."],
  ["Disclosure policy (PSTI)", "A published, monitored contact with a committed acknowledgement timeframe."],
  ["Update period transparency (PSTI)", "The minimum security update period published at point of sale."],
  ["GB conformity documentation (UKCA)", "A GB-specific statement of compliance and technical documentation, kept current."],
];

export default function MachineryUkca() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen">
      <SiteHeader />
      <section className="blueprint border-b border-slate-200">
        <div className="mx-auto max-w-4xl px-5 py-16 lg:px-8 lg:py-24">
          <p className="overline text-navy-800">EU Machinery Regulation 2023/1230 · UKCA route · UK PSTI</p>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight text-navy-900 sm:text-5xl">
            When security failures become safety failures, and when GB has its own rules
          </h1>
          <p className="mt-6 text-base leading-relaxed text-slate-600">
            Two additional readiness views for the same product profile. The Machinery Regulation view
            focuses on whether a security weakness can defeat a safety function. The GB views
            focuses on the GB market route: default credentials, disclosure, published update periods
            and GB conformity documentation.
          </p>
          <Button
            size="lg"
            className="mt-9 bg-navy-800 text-white hover:bg-navy-700"
            onClick={() => navigate("/register")}
            data-testid="mr-ukca-start-button"
          >
            Start a readiness check <ArrowRight size={17} className="ml-2" />
          </Button>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-8 px-5 py-16 lg:grid-cols-2 lg:px-8">
        <div className="border border-slate-200 bg-white p-8">
          <Gear size={26} weight="duotone" className="text-navy-800" />
          <p className="mt-4 overline text-navy-800">EU Machinery Regulation</p>
          <h2 className="mt-2 font-display text-xl font-bold text-navy-900">Safety-related control systems</h2>
          <ul className="mt-5 space-y-4">
            {MR.map(([t, b]) => (
              <li key={t}>
                <p className="text-sm font-semibold text-navy-900">{t}</p>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{b}</p>
              </li>
            ))}
          </ul>
        </div>
        <div className="border border-slate-200 bg-white p-8">
          <Certificate size={26} weight="duotone" className="text-navy-800" />
          <p className="mt-4 overline text-navy-800">UKCA route &amp; UK PSTI</p>
          <h2 className="mt-2 font-display text-xl font-bold text-navy-900">Two separate GB obligations</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            UKCA is a conformity-assessment route: a statement of compliance and technical documentation for the
            GB market. UK PSTI is a product security regime for consumer connectable products. We score them
            separately so a conformity file is never mistaken for security compliance.
          </p>
          <ul className="mt-5 space-y-4">
            {UKCA.map(([t, b]) => (
              <li key={t}>
                <p className="text-sm font-semibold text-navy-900">{t}</p>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{b}</p>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs leading-relaxed text-slate-500">
            Both appear once your product profile declares the UK market. PSTI additionally requires the product
            to be connectable; otherwise it is reported as not applicable.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 pb-20 lg:px-8">
        <div className="border border-slate-200 bg-slate-100/70 p-6 text-sm leading-relaxed text-slate-600">
          The Machinery Regulation view is driven by your product profile: select the machinery /
          safety-related control system category, or declare a safety function, and it scores as fully
          applicable. Otherwise it is shown with a limited applicability flag for awareness. No
          regulation or standard text is reproduced anywhere in the assessment or report.
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
